import type { AssignableRole, MembershipRole, WorkspaceRole } from "@unbox/api";
import { useState } from "react";

// UNB-2001 — shared state for the workspace → merchants role tree used by the
// Add/Edit user + Add-users-to-workspace surfaces.
//
// Brand membership = key presence in `merchants`; the value is the per-brand
// `membership_role`. `workspaceAdmin` grants authority over the whole workspace
// (per-merchant roles are moot while it's on). This maps 1:1 onto the backend
// `roleGrants` shape consumed by the invite / addUsers / workspace-memberships
// endpoints.
export type WorkspaceGrant = {
  workspaceAdmin: boolean;
  merchants: Record<string, MembershipRole>;
};
export type WorkspaceRoleGrants = Record<string, WorkspaceGrant>;

/** Workspace-level dropdown value, incl. the derived, display-only "multiple". */
export type WorkspaceLevelRole = AssignableRole | "multiple";

const mapAll = (merchants: Record<string, MembershipRole>, role: MembershipRole): Record<string, MembershipRole> =>
  Object.fromEntries(Object.keys(merchants).map((id) => [id, role]));

/**
 * Build the tree's grant state from an existing user's workspaces (Edit user).
 * `role` is the workspace membership role; each merchant carries its per-brand role.
 */
export function grantsFromUserWorkspaces(
  workspaces: { id: string; role: WorkspaceRole; merchants: { id: string; role: MembershipRole }[] }[],
): WorkspaceRoleGrants {
  const grants: WorkspaceRoleGrants = {};
  for (const ws of workspaces) {
    grants[ws.id] = {
      workspaceAdmin: ws.role === "workspace_admin",
      merchants: Object.fromEntries(ws.merchants.map((m) => [m.id, m.role])),
    };
  }
  return grants;
}

/** Deep-equality for a single grant (workspaceAdmin + per-merchant roles). */
export function grantsEqual(a: WorkspaceGrant | undefined, b: WorkspaceGrant | undefined): boolean {
  if (!a || !b) return a === b;
  if (a.workspaceAdmin !== b.workspaceAdmin) return false;
  const ak = Object.keys(a.merchants);
  const bk = Object.keys(b.merchants);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a.merchants[k] === b.merchants[k]);
}

/** The effective workspace-level role for a grant — "multiple" when brands diverge. */
export function deriveWorkspaceRole(grant: WorkspaceGrant): WorkspaceLevelRole {
  if (grant.workspaceAdmin) return "workspace_admin";
  const roles = Object.values(grant.merchants);
  if (roles.length === 0) return "user";
  if (roles.every((r) => r === "merchant_admin")) return "merchant_admin";
  if (roles.every((r) => r === "user")) return "user";
  return "multiple";
}

export function useWorkspaceRoleTree(initial: WorkspaceRoleGrants = {}) {
  const [grants, setGrants] = useState<WorkspaceRoleGrants>(initial);

  /** Add a workspace granting every listed brand at role `user`. */
  const addWorkspace = (workspaceId: string, merchantIds: string[]) =>
    setGrants((prev) => ({
      ...prev,
      [workspaceId]: {
        workspaceAdmin: false,
        merchants: Object.fromEntries(merchantIds.map((id) => [id, "user" as MembershipRole])),
      },
    }));

  const removeWorkspace = (workspaceId: string) =>
    setGrants((prev) => {
      const next = { ...prev };
      delete next[workspaceId];
      return next;
    });

  /** Toggle a brand's membership; new grants default to the workspace's role. */
  const toggleBrand = (workspaceId: string, merchantId: string) =>
    setGrants((prev) => {
      const grant = prev[workspaceId] ?? { workspaceAdmin: false, merchants: {} };
      const merchants = { ...grant.merchants };
      if (merchantId in merchants) {
        delete merchants[merchantId];
      } else {
        const derived = deriveWorkspaceRole(grant);
        merchants[merchantId] = derived === "merchant_admin" ? "merchant_admin" : "user";
      }
      return { ...prev, [workspaceId]: { ...grant, merchants } };
    });

  /**
   * Set the workspace-level role; cascades to every child brand.
   *
   * `allMerchantIds` is the workspace's full roster, not the brands already granted.
   * Merchant Admin has to reach every brand the workspace owns to match what Workspace
   * Admin covers, and a user may hold only a subset — mapping over `grant.merchants`
   * alone would leave the ungranted brands untouched. Demoting to `user` keeps to the
   * granted set, since demoting must not hand out access.
   */
  const setWorkspaceRole = (workspaceId: string, role: AssignableRole, allMerchantIds: string[]) =>
    setGrants((prev) => {
      const grant = prev[workspaceId];
      if (!grant) return prev;
      if (role === "workspace_admin") {
        return { ...prev, [workspaceId]: { workspaceAdmin: true, merchants: mapAll(grant.merchants, "user") } };
      }
      if (role === "merchant_admin") {
        const merchants = Object.fromEntries(allMerchantIds.map((id) => [id, "merchant_admin" as MembershipRole]));
        return { ...prev, [workspaceId]: { workspaceAdmin: false, merchants } };
      }
      return { ...prev, [workspaceId]: { workspaceAdmin: false, merchants: mapAll(grant.merchants, "user") } };
    });

  /** Set a single brand's role — flips the workspace to "multiple" if it diverges. */
  const setMerchantRole = (workspaceId: string, merchantId: string, role: MembershipRole) =>
    setGrants((prev) => {
      const grant = prev[workspaceId];
      if (!grant || !(merchantId in grant.merchants)) return prev;
      return {
        ...prev,
        [workspaceId]: { workspaceAdmin: false, merchants: { ...grant.merchants, [merchantId]: role } },
      };
    });

  return { grants, setGrants, addWorkspace, removeWorkspace, toggleBrand, setWorkspaceRole, setMerchantRole };
}
